import axios from 'axios'
import { config } from '../config'
import authClient from '../auth/authClient'
import {
	getTenantContextSnapshot,
	isTenantGenerationCurrent,
	notifyTenantAccessInvalidated,
} from '../tenancy/tenantContext'
import {
	REQUEST_SCOPE,
	classifyApiRequest,
	isGenerationBoundRequest,
} from '../tenancy/tenantRequestPolicy'

export const ORGANIZATION_HEADER = 'X-Reldo-Organization-Id'

export class OrganizationContextRequiredError extends Error {
	constructor() {
		super('A validated organization context is required for this request')
		this.name = 'OrganizationContextRequiredError'
		this.code = 'ORGANIZATION_CONTEXT_REQUIRED'
	}
}

export class StaleOrganizationResponseError extends Error {
	constructor() {
		super('Response belongs to a stale organization context')
		this.name = 'StaleOrganizationResponseError'
		this.code = 'STALE_ORGANIZATION_RESPONSE'
	}
}

const apiClient = axios.create({ baseURL: config.apiUrl })
const contextCandidates = new Map()
let nextContextCandidateId = 1

function positiveId(value) {
	const normalized = String(value ?? '')
	return /^[1-9]\d*$/.test(normalized) ? normalized : null
}

function removeOrganizationHeaders(headers) {
	if (!headers) return
	if (typeof headers.delete === 'function') {
		headers.delete(ORGANIZATION_HEADER)
		return
	}
	for (const name of Object.keys(headers)) {
		if (name.toLowerCase() === ORGANIZATION_HEADER.toLowerCase()) delete headers[name]
	}
}

function setOrganizationHeader(headers, organizationId) {
	removeOrganizationHeaders(headers)
	if (typeof headers.set === 'function') headers.set(ORGANIZATION_HEADER, organizationId)
	else headers[ORGANIZATION_HEADER] = organizationId
}

apiClient.interceptors.request.use(request => {
	request.headers = request.headers || {}
	removeOrganizationHeaders(request.headers)
	const token = authClient.getAccessToken()
	if (token) request.headers.Authorization = `Bearer ${token}`

	const classification = classifyApiRequest(request)
	request._reldoRequestScope = classification.scope
	request._reldoRequestFamily = classification.family

	if (classification.scope === REQUEST_SCOPE.TENANT) {
		const snapshot = getTenantContextSnapshot()
		if (!snapshot.organizationId) throw new OrganizationContextRequiredError()
		request._organizationGeneration = snapshot.generation
		setOrganizationHeader(request.headers, snapshot.organizationId)
	}

	if (classification.scope === REQUEST_SCOPE.ORGANIZATION_CONTEXT) {
		const candidate = contextCandidates.get(request._reldoContextCandidateId)
		if (!candidate || !isTenantGenerationCurrent(candidate.generation)) {
			throw new OrganizationContextRequiredError()
		}
		request._organizationGeneration = candidate.generation
		setOrganizationHeader(request.headers, candidate.organizationId)
	}

	return request
})

apiClient.interceptors.response.use(
	response => {
		if (
			isGenerationBoundRequest(response.config)
			&& !isTenantGenerationCurrent(response.config._organizationGeneration)
		) throw new StaleOrganizationResponseError()
		return response
	},
	async error => {
		const originalRequest = error.config
		if (
			originalRequest
			&& isGenerationBoundRequest(originalRequest)
			&& !isTenantGenerationCurrent(originalRequest._organizationGeneration)
		) return Promise.reject(new StaleOrganizationResponseError())

		const organizationErrorCode = error.response?.data?.error?.code
		if (
			originalRequest
			&& originalRequest._reldoRequestScope === REQUEST_SCOPE.TENANT
			&& error.response?.status === 403
			&& organizationErrorCode === 'ORGANIZATION_CONTEXT_FORBIDDEN'
		) {
			notifyTenantAccessInvalidated({
				code: organizationErrorCode,
				generation: originalRequest._organizationGeneration,
			})
		}
		if (!originalRequest || error.response?.status !== 401 || originalRequest._authRetried) {
			return Promise.reject(error)
		}

		originalRequest._authRetried = true
		try {
			const token = await authClient.refresh()
			if (
				isGenerationBoundRequest(originalRequest)
				&& !isTenantGenerationCurrent(originalRequest._organizationGeneration)
			) return Promise.reject(new StaleOrganizationResponseError())
			originalRequest.headers = originalRequest.headers || {}
			originalRequest.headers.Authorization = `Bearer ${token}`
			return apiClient(originalRequest)
		} catch (refreshError) {
			authClient.notifySessionExpired()
			return Promise.reject(refreshError)
		}
	}
)

// The candidate selector is deliberately accepted only by this narrow helper.
// Arbitrary Axios config cannot provide or replace an Organization header.
export function requestOrganizationContext(organizationId, generation, requestConfig = {}) {
	const normalized = positiveId(organizationId)
	if (!normalized || !Number.isInteger(generation)) throw new OrganizationContextRequiredError()
	const candidateId = nextContextCandidateId++
	contextCandidates.set(candidateId, { organizationId: normalized, generation })
	return apiClient.get('/api/organization-context', {
		...requestConfig,
		_reldoContextCandidateId: candidateId,
	}).finally(() => contextCandidates.delete(candidateId))
}

export default apiClient
